import { useEffect, useRef } from 'react';
import type { EventSourcePolyfill } from 'event-source-polyfill';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '@/features/auth/store';
import i18n from '@/i18n';
import { applyOrderEventToCaches } from '@/features/orders';
import {
  ORDER_DETAIL_PATH,
  isOrderDetailPath,
  isUnderOrdersScope,
} from '@/shared/lib/routes';
import { notificationsApi } from '../api';
import { useNotificationsStore } from '../store';
import type { NotificationResponse, ScheduledReminderEvent } from '../types';
import { notificationsKeys } from './useNotifications';
import { playNotificationSound } from '../utils/sound';
import { showDesktopNotification } from '../utils/desktopNotification';
import { useOrderAlertStore } from '@/features/orders/store/orderAlertStore';

/**
 * Debounce window after SSE open during which we treat incoming events as the
 * backfill burst (oldest-first replay of last-24h unread). After this idle
 * window elapses, further events are treated as real-time and trigger
 * toast/sound feedback.
 */
const FLUSH_IDLE_MS = 1500;
const FIRST_EVENT_GRACE_MS = 3000;
/** Consecutive onerror events without a successful open before we warn the user. */
const FAILURE_STREAK_WARN_THRESHOLD = 5;
/** Exponential backoff bounds for manual reconnect: 1s, 2s, 4s, … capped at 30s, ±500ms jitter. */
const RECONNECT_BASE_DELAY_MS = 1_000;
const RECONNECT_MAX_DELAY_MS = 30_000;
const RECONNECT_JITTER_MS = 500;
/**
 * localStorage key written by any tab that receives a 429 so that all other
 * open tabs respect the same backoff window instead of piling on independently.
 */
const RATE_LIMIT_UNTIL_KEY = 'rh:notifications:rl';

/**
 * Owns the EventSource lifecycle for the given restaurant. Seeds the store
 * via listNotifications, opens the SSE stream, and routes real-time events:
 *  - ORDER_PLACED → ring + route-aware toast (navigate / highlight); does NOT trigger printing
 *  - ORDER_ACCEPTED → orders feature patches its caches; triggers auto-print
 *  - ORDER_REJECTED → orders feature patches its caches
 */
export function useNotificationStream(restaurantId: string | undefined) {
  // Subscribe to presence of a token (login/logout) rather than the token string
  // itself. Silent refreshes rotate the string without changing auth state, and
  // putting the string in the dep array caused the effect to re-run on every
  // refresh — cancelling the open SSE, resetting reconnectAttempt to 0, and
  // locking reconnects at 1 s forever. Token is read from the store at connect
  // time instead so openStream always uses the latest credential.
  const hasToken = useAuthStore((s) => !!s.token);
  const {
    bind,
    unbind,
    seedList,
    receive,
    setConnectionStatus,
    setInitialLoadState,
    setInitialFlush,
  } = useNotificationsStore(
    useShallow((s) => ({
      bind: s.bind,
      unbind: s.unbind,
      seedList: s.seedList,
      receive: s.receive,
      setConnectionStatus: s.setConnectionStatus,
      setInitialLoadState: s.setInitialLoadState,
      setInitialFlush: s.setInitialFlush,
    }))
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sourceRef = useRef<EventSourcePolyfill | null>(null);
  const errorStreakRef = useRef(0);
  const hasWarnedOfflineRef = useRef(false);

  useEffect(() => {
    if (!restaurantId || !hasToken) return;

    bind(restaurantId);
    errorStreakRef.current = 0;
    hasWarnedOfflineRef.current = false;
    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempt = 0;

    const scheduleFlushEnd = (delay: number) => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => {
        if (!cancelled) setInitialFlush(false);
      }, delay);
    };

    const openStream = () => {
      if (cancelled) return;

      // If any tab received a 429 recently, honour the shared backoff window
      // so multiple tabs don't independently hammer the endpoint.
      try {
        const sharedUntil = Number(localStorage.getItem(RATE_LIMIT_UNTIL_KEY) ?? 0);
        if (Date.now() < sharedUntil) {
          const remaining = sharedUntil - Date.now() + Math.floor(Math.random() * RECONNECT_JITTER_MS);
          reconnectTimer = setTimeout(openStream, remaining);
          return;
        }
      } catch { /* localStorage unavailable (private browsing, quota) — proceed */ }

      setConnectionStatus('connecting');
      setInitialFlush(true);
      // Grace timer is intentionally NOT started here. We start it in onopen
      // instead, so that exponential-backoff delays and network latency don't
      // eat the window before any backfill events arrive.

      const currentToken = useAuthStore.getState().token;
      if (!currentToken) return;
      const source = notificationsApi.openNotificationStream(restaurantId, currentToken);
      sourceRef.current = source;

      source.onopen = () => {
        // Successful connection — clear the shared rate-limit fence so other
        // tabs can also attempt to connect without waiting out the full window.
        try { localStorage.removeItem(RATE_LIMIT_UNTIL_KEY); } catch { /* storage unavailable */ }
        errorStreakRef.current = 0;
        if (hasWarnedOfflineRef.current) {
          toast.success(i18n.t('notifications.reconnected'));
          hasWarnedOfflineRef.current = false;
        }
        setConnectionStatus('open');
        // Start the flush grace window from here — the connection is now open
        // and the server will immediately begin streaming backfill. Starting
        // earlier (at openStream() call time) meant exponential-backoff delays
        // and network handshake time consumed most of the window before any
        // events arrived, letting backfill events escape into real-time mode.
        scheduleFlushEnd(FIRST_EVENT_GRACE_MS);
        // Reset the backoff counter only after the connection has been stable for a few
        // seconds. Resetting immediately on onopen (which fires on the 200 header alone)
        // means a server that closes the stream right after opening never triggers backoff —
        // the counter resets each cycle, locking reconnects at 1 s forever.
        const openedAt = Date.now();
        const stabilityCheck = setTimeout(() => {
          if (!cancelled && sourceRef.current === source) {
            reconnectAttempt = 0;
          }
        }, 5_000);
        source.addEventListener('abort', () => clearTimeout(stabilityCheck));
        void openedAt;

        // Always re-seed via REST on open. The 24h-unread SSE backfill
        // misses (a) older items, (b) cross-device read marks, and (c) any
        // events that landed between the initial seed call and the SSE
        // handshake. Cost is one redundant list call per (re)connect.
        notificationsApi
          .listNotifications(restaurantId, { limit: 20 })
          .then((payload) => {
            if (cancelled) return;
            seedList(payload);
            setInitialLoadState('ok');
            queryClient.setQueryData(notificationsKeys.list(restaurantId, false), payload);
          })
          .catch(() => {
            // Next onopen will try again; don't spam toasts here.
          });
      };

      source.onerror = async (event: Event) => {
        if (cancelled) return;
        // Guard against concurrent invocations: the SSE polyfill can fire
        // onerror multiple times before the first async handler finishes
        // awaiting refreshTokens(). Without this check, each invocation
        // schedules its own reconnect timer → burst of connections → 429s.
        if (sourceRef.current !== source) return;

        // Take manual control of reconnect: close so the polyfill's own
        // retry loop doesn't race our exponential backoff.
        source.close();
        sourceRef.current = null;

        setConnectionStatus('closed');
        errorStreakRef.current += 1;

        const httpStatus = (event as { status?: number }).status;

        // 429 means the backend is rate-limiting us. Skip the token refresh
        // (it won't help) and honour the server's Retry-After: 60 window.
        if (httpStatus !== 429) {
          // Await the token refresh so the reconnect always uses a fresh credential.
          // Not awaiting caused a race: the old (possibly expired) token was used for
          // the next SSE open, and the onopen REST call fired before the new token
          // landed in the store, producing a burst of 403s.
          await useAuthStore.getState().refreshTokens();

          // Re-check cancelled: the effect may have been torn down while we awaited.
          if (cancelled) return;
        }

        if (
          errorStreakRef.current >= FAILURE_STREAK_WARN_THRESHOLD &&
          !hasWarnedOfflineRef.current
        ) {
          hasWarnedOfflineRef.current = true;
          toast.warning(i18n.t('notifications.offlineTitle'), {
            description: i18n.t('notifications.offlineDescription'),
          });
        }

        let delay: number;
        if (httpStatus === 429) {
          // Retry-After header is 60s — back off for ~65s to avoid thrashing.
          // Write to localStorage so all open tabs respect the same window.
          try { localStorage.setItem(RATE_LIMIT_UNTIL_KEY, String(Date.now() + 65_000)); } catch { /* storage unavailable */ }
          delay = 65_000 + Math.floor(Math.random() * RECONNECT_JITTER_MS);
        } else {
          delay =
            Math.min(
              RECONNECT_MAX_DELAY_MS,
              RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempt
            ) + Math.floor(Math.random() * RECONNECT_JITTER_MS);
          reconnectAttempt += 1;
        }
        reconnectTimer = setTimeout(openStream, delay);
      };

      source.addEventListener('notification', (evt) => {
        const msgEvent = evt as MessageEvent;
        let raw: unknown;
        try {
          raw = JSON.parse(msgEvent.data);
        } catch {
          return;
        }

        // Reminder events are ephemeral — they carry no notificationId and must
        // not be inserted into the bell store.
        if (
          typeof raw === 'object' &&
          raw !== null &&
          'eventType' in raw &&
          (
            (raw as { eventType: string }).eventType === 'SCHEDULED_ORDER_REMINDER_90' ||
            (raw as { eventType: string }).eventType === 'SCHEDULED_ORDER_REMINDER_60'
          )
        ) {
          if (!useNotificationsStore.getState().isInitialFlush) {
            fireScheduledReminderFeedback(raw as ScheduledReminderEvent, restaurantId, navigate);
          }
          return;
        }

        const payload = raw as NotificationResponse;
        const wasFlush = useNotificationsStore.getState().isInitialFlush;
        receive(payload);
        scheduleFlushEnd(FLUSH_IDLE_MS);

        if (!wasFlush) {
          if (payload.eventType === 'ORDER_PLACED') {
            fireOrderPlacedFeedback(payload, restaurantId, navigate);
          } else if (payload.eventType === 'SCHEDULED_ORDER_PLACED') {
            fireScheduledOrderPlacedFeedback(payload, restaurantId, navigate);
          } else if (payload.eventType === 'SCHEDULED_ORDER_RELEASED') {
            fireScheduledOrderReleasedFeedback(payload, restaurantId, navigate);
          }
          applyOrderEventToCaches(queryClient, restaurantId, payload);
        } else if (
          payload.eventType === 'ORDER_PLACED' ||
          payload.eventType === 'SCHEDULED_ORDER_RELEASED'
        ) {
          // Backfill event — suppress the alert but mark the order as seen so
          // subsequent SSE reconnects (within the same tab session) don't
          // re-trigger the dialog for the same pending order.
          useOrderAlertStore.getState().markSeen(payload.orderId);
        }
        queryClient.invalidateQueries({ queryKey: notificationsKeys.listRoot(restaurantId) });
      });
    };

    const seedThenConnect = async () => {
      setInitialLoadState('loading');
      try {
        const payload = await notificationsApi.listNotifications(restaurantId, { limit: 20 });
        if (cancelled) return;
        seedList(payload);
        setInitialLoadState('ok');
        queryClient.setQueryData(notificationsKeys.list(restaurantId, false), payload);
      } catch {
        if (!cancelled) setInitialLoadState('error');
      }

      if (cancelled) return;
      openStream();
    };

    seedThenConnect();

    // Background-tab throttling can stall SSE delivery without firing onerror.
    // When the tab refocuses, reconcile via REST in case we drifted.
    const onVisibilityChange = () => {
      if (cancelled) return;
      if (typeof document === 'undefined' || document.hidden) return;
      notificationsApi
        .listNotifications(restaurantId, { limit: 20 })
        .then((payload) => {
          if (cancelled) return;
          seedList(payload);
          queryClient.setQueryData(notificationsKeys.list(restaurantId, false), payload);
        })
        .catch(() => {
          // Stream's own reconnect loop will recover; no toast here.
        });
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      cancelled = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
      unbind();
    };
  }, [restaurantId, hasToken, bind, unbind, seedList, receive, setConnectionStatus, setInitialLoadState, setInitialFlush, queryClient, navigate]);
}

function fireScheduledReminderFeedback(
  event: ScheduledReminderEvent,
  restaurantId: string,
  navigate: ReturnType<typeof useNavigate>
): void {
  const is60 = event.eventType === 'SCHEDULED_ORDER_REMINDER_60';
  const { orderShortId, customerName, scheduledFor, total, currency } = event.payload;

  const store = useNotificationsStore.getState();
  if (is60) {
    store.addReminder60(event.orderId);
    playNotificationSound({ onPlayed: () => useNotificationsStore.getState().unlockSound() });
  } else {
    store.addReminder90(event.orderId);
  }

  store.highlightOrder(event.orderId);

  const timeLabel = new Date(scheduledFor).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const moneyLabel = `${total.toFixed(2)} ${currency}`;
  const customerLabel = customerName ?? i18n.t('orders.guest');
  const metaLine = i18n.t('notifications.scheduledReminderMeta', {
    customer: customerLabel,
    amount: moneyLabel,
    time: timeLabel,
  });

  showDesktopNotification({
    title: is60
      ? i18n.t('notifications.scheduledReminder60Title', { shortId: orderShortId })
      : i18n.t('notifications.scheduledReminder90Title', { shortId: orderShortId }),
    body: metaLine,
    tag: `scheduled-reminder:${event.orderId}`,
    onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, event.orderId)),
  });

  if (is60) {
    toast.warning(i18n.t('notifications.scheduledReminder60Toast', { shortId: orderShortId }), {
      description: metaLine,
      duration: 15000,
      closeButton: true,
      action: {
        label: i18n.t('notifications.startPreparingAction'),
        onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, event.orderId)),
      },
    });
  } else {
    toast(i18n.t('notifications.scheduledReminder90Toast', { shortId: orderShortId }), {
      description: metaLine,
      duration: 10000,
      closeButton: true,
      action: {
        label: i18n.t('notifications.viewAction'),
        onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, event.orderId)),
      },
    });
  }
}

function fireScheduledOrderPlacedFeedback(
  n: NotificationResponse,
  restaurantId: string,
  navigate: ReturnType<typeof useNavigate>
): void {
  playNotificationSound({ onPlayed: () => useNotificationsStore.getState().unlockSound() });
  showDesktopNotification({
    title: n.title,
    body: n.body,
    tag: `order:${n.orderId}`,
    onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, n.orderId)),
  });
  toast(n.title, {
    description: n.body,
    duration: 8000,
    closeButton: true,
    action: {
      label: i18n.t('notifications.viewAction'),
      onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, n.orderId)),
    },
  });
}

function fireScheduledOrderReleasedFeedback(
  n: NotificationResponse,
  restaurantId: string,
  navigate: ReturnType<typeof useNavigate>
): void {
  // Scheduled order is now live — treat exactly like a new order.
  useOrderAlertStore.getState().enqueue(n.orderId, restaurantId);
  playNotificationSound({ onPlayed: () => useNotificationsStore.getState().unlockSound() });
  showDesktopNotification({
    title: n.title ?? i18n.t('notifications.scheduledOrderReadyTitle'),
    body: n.body ?? i18n.t('notifications.scheduledOrderReadyBody'),
    tag: `order:${n.orderId}`,
    onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, n.orderId)),
  });
  toast.warning(n.title ?? i18n.t('notifications.scheduledOrderReadyTitle'), {
    description: n.body ?? i18n.t('notifications.scheduledReleasedBody'),
    duration: 10000,
    closeButton: true,
    action: {
      label: i18n.t('notifications.viewAction'),
      onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, n.orderId)),
    },
  });
}

function fireOrderPlacedFeedback(
  n: NotificationResponse,
  restaurantId: string,
  navigate: ReturnType<typeof useNavigate>
): void {
  const state = useNotificationsStore.getState();

  // Push to the alert queue — the OrderAlertDialog takes it from here,
  // including persistent ringing until acknowledged.
  useOrderAlertStore.getState().enqueue(n.orderId, restaurantId);

  // Fallback sound for the brief window before the dialog mounts.
  playNotificationSound({
    onPlayed: () => useNotificationsStore.getState().unlockSound(),
  });

  // OS-level notification when the tab is hidden — sonner toasts don't
  // surface there. No-op when the tab is focused or permission is missing.
  showDesktopNotification({
    title: n.title,
    body: n.body,
    tag: `order:${n.orderId}`,
    onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, n.orderId)),
  });

  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const suppressRedirect =
    isOrderDetailPath(pathname, restaurantId, n.orderId) ||
    isUnderOrdersScope(pathname, restaurantId);

  if (suppressRedirect) {
    state.highlightOrder(n.orderId);
    toast(n.title, {
      description: n.body,
      duration: 6000,
      closeButton: true,
    });
    return;
  }

  toast(n.title, {
    description: n.body,
    duration: 8000,
    closeButton: true,
    action: {
      label: i18n.t('notifications.viewAction'),
      onClick: () => navigate(ORDER_DETAIL_PATH(restaurantId, n.orderId)),
    },
  });
}

